// The single-seat subscription family being previewed. Creative covers creative images only;
// editorial is the superset covering both image families.
export enum SubscriptionPreviewFamilyEnum {
  CREATIVE = 'creative',
  EDITORIAL = 'editorial',
}

/**
 * The server's authoritative single-seat subscription preview decision. Modelled as a union
 * because the family only exists while a preview is active. `sku` is optional: the server finds it
 * from the subscription family, and there may be no plan for that family (the visitor's country
 * doesn't sell it, or a signed-in customer can't buy it) — the preview still shows, and the banner
 * then drops "Subscribe now".
 *
 * `includedFamilies` and `includesVideo` are the plan's boundaries, so the search surfaces can
 * say when the current tab falls outside it. Both are optional because they were added to the
 * server payload after the banner shipped.
 * */
export type SubscriptionPreview =
  | Readonly<{ inPreview: false }>
  | Readonly<{
      inPreview: true;
      family: SubscriptionPreviewFamilyEnum;
      sku?: string;
      includedFamilies?: readonly SubscriptionPreviewFamilyEnum[];
      includesVideo?: boolean;
    }>;

// The visitor's authentication state. Three states rather than a signed-in
// boolean, because a visitor can be known without being authenticated:
// remembered from a prior session, but not authenticated in this one. An enum
// keeps the three exhaustive and mutually exclusive, so no consumer has to
// reason about a combination that cannot occur.
export enum AuthStateEnum {
  ANONYMOUS = 'anonymous',
  KNOWN = 'known',
  AUTHENTICATED = 'authenticated',
}

export type SearchProfileDTO = Readonly<{
  // Whether the market offers the nudity filter at all.
  allowsNudity: boolean;
  // The default the nudity filter takes when the visitor has stated nothing.
  // Not a preference: a stored choice wins over it, and only "never stated"
  // falls through. Keeping the two apart is the point — a source that
  // substituted the default before answering would leave nothing able to tell
  // them apart.
  excludeNudity: boolean;
  authState: AuthStateEnum;
  isSubscriptionRepackagingActive: boolean;
  upsellTestEligible: boolean;
  hasNLSKeywordInterleaveForMostPopular: boolean;
  hasCustomMediaAgreements: boolean;
  ezaCompUser: boolean;
  hasEssentialSubscription: boolean;
  hasGeoPricePromo: boolean;
  isMobile: boolean;
  isPaPreview: boolean;
  // Gates bot/first-time-visitor-sensitive behavior
  botOrFirstTimeVisitor: boolean;
  subscriptionPreview?: SubscriptionPreview;
  locale?: string;
  // Whether the visitor holds a royalty-free subscription, which keeps them out of the single-seat
  // audience. Optional because only the Rails payload carries it.
  hasRfSubscription?: boolean;
}>;

/**
 * The part of the profile the BFF answers today.
 *
 * A projection of the model rather than a type of its own: the profile is the
 * profile, and this says which of its fields `GET /search-profile` currently
 * owns; the server-rendered payload still supplies the rest. As fields migrate,
 * this `Pick` widens — no new type, no parallel concept to keep in step.
 */
export type ResolvedSearchProfile = Pick<
  SearchProfileDTO,
  'allowsNudity' | 'excludeNudity' | 'authState' | 'botOrFirstTimeVisitor'
>;

export interface SearchProfileMethods {
  prefersSignatureCrown(): boolean;
  isAuthenticated(): boolean;
}

type CollectionConfig = {
  showCollectionDropdown: boolean;
  showCollectionTabs: boolean;
  showCollectionPanelFilter: boolean;
  showCollectionPill: boolean;
};

type FeatureConfig = {
  collection: CollectionConfig;
};

export type SearchProfile = SearchProfileDTO &
  SearchProfileMethods & {
    featureConfig: FeatureConfig;
  };

const prefersSignatureCrown = (dto: SearchProfileDTO): boolean =>
  dto.isSubscriptionRepackagingActive || dto.upsellTestEligible;

const isAuthenticated = (dto: SearchProfileDTO): boolean =>
  dto.authState === AuthStateEnum.AUTHENTICATED;

const methods = (dto: SearchProfileDTO): SearchProfileMethods => ({
  prefersSignatureCrown: () => prefersSignatureCrown(dto),
  isAuthenticated: () => isAuthenticated(dto),
});

/**
 * Single source of truth for the collection feature config. Visibility of the
 * dropdown / tabs / panel filter / pill is driven by iSSubsRepackaging
 * (`isSubscriptionRepackagingActive`).
 *
 * Basic/essential subscribers (active rf subscription without signature) are a special
 * case: the iSSubsRepackaging flag is active for them but they should retain the legacy
 * collection toggle and must not see the horizontal pill or produce removable
 * applied-filter pills.
 * */
const buildCollectionFeatureConfig = (
  dto: SearchProfileDTO
): CollectionConfig => ({
  showCollectionDropdown:
    dto.isSubscriptionRepackagingActive && !dto.hasEssentialSubscription,
  showCollectionTabs:
    !dto.isSubscriptionRepackagingActive || dto.hasEssentialSubscription,
  showCollectionPanelFilter: dto.isSubscriptionRepackagingActive,
  showCollectionPill:
    dto.isSubscriptionRepackagingActive && !dto.hasEssentialSubscription,
});

export class SearchProfileFactory {
  fromDTO = (dto: SearchProfileDTO): SearchProfile => ({
    ...dto,
    ...methods(dto),
    featureConfig: {
      collection: buildCollectionFeatureConfig(dto),
    },
  });
}
